/**
 * Large files in chunks (1.5.5): a video of up to 300 MB goes up a piece at a time with a progress bar. When the
 * connection drops, the uploader asks the server where it got to and carries on from there — a chunk is never sent
 * twice into the file, and a page closed half way picks up the same upload when the same file is chosen again.
 */
export interface UploadTransport {
  start(): Promise<{ uploadId: string; name: string }>;
  status(uploadId: string): Promise<{ next: number; done: boolean }>;
  put(uploadId: string, start: number, chunk: Blob): Promise<{ next: number; done: boolean }>;
}

export interface ChunkOptions {
  chunkSize: number;
  onProgress?: (sent: number, size: number) => void;
  /** Called once the server has given the upload its id (save it to resume after the page closes). */
  onStarted?: (u: { uploadId: string; name: string }) => void;
  /** An upload already started for this file. */
  resume?: { uploadId: string; name: string } | null;
  maxRetries?: number;
  wait?: (ms: number) => Promise<void>;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** A refusal that trying again cannot fix (the server said no, not "where were we?"): stop at once. */
function fatal(e: unknown): boolean {
  const status = (e as { status?: unknown } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500 && ![408, 409, 429].includes(status);
}

export async function uploadInChunks(file: Blob, t: UploadTransport, o: ChunkOptions): Promise<{ uploadId: string; name: string }> {
  const wait = o.wait ?? sleep;
  const maxRetries = o.maxRetries ?? 8;
  const size = file.size;
  let failures = 0;
  const backoff = () => wait(Math.min(30_000, 1000 * 2 ** Math.min(failures - 1, 5)));

  let up = o.resume ?? null;
  let next = 0;
  let known = false;
  if (!up) {
    up = await t.start();
    o.onStarted?.(up);
    known = true;
  }
  for (;;) {
    if (!known) {
      try {
        const s = await t.status(up.uploadId);
        next = s.next;
        known = true;
        if (s.done) break;
      } catch (e) {
        if (fatal(e) || ++failures > maxRetries) throw e;
        await backoff();
        continue;
      }
    }
    if (next >= size) break;
    try {
      const r = await t.put(up.uploadId, next, file.slice(next, Math.min(size, next + o.chunkSize)));
      next = r.next;
      failures = 0;
      o.onProgress?.(Math.min(next, size), size);
      if (r.done) break;
    } catch (e) {
      if (fatal(e) || ++failures > maxRetries) throw e;
      known = false;
      await backoff();
    }
  }
  o.onProgress?.(size, size);
  return up;
}
